import { Icons } from "assets";
import classNames from "classnames";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import useWindowDimensions from "shared/hooks/useWindowDimensions";
import ConfirmationModal from "shared/modal/confirmation";
import { resetAuthReducer } from "shared/redux/reducers/authSlice";
import { routeConstant } from "shared/routes/routeConstant";
import { toastMessage } from "../toast";
import NavItem from "./navItem";
import styles from "./style.module.scss";
import { LogoutService } from "shared/services/auth";
import Switch from "../switch";
import { resetTheme, setTheme } from "shared/redux/reducers/theme";
import { resetQuestionnaireReducer } from "shared/redux/reducers/questionnaire";
import { resetCountReducer } from "shared/redux/reducers/count";

interface Props {
  sidebarArr: SideBarItemsType[];
}

function DashboardSidebar({ sidebarArr }: Props) {
  const {
    auth: { user },
    theme: { theme },
  } = useSelector((state: any) => state?.root);
  const dispatch = useDispatch();
  const { width } = useWindowDimensions();
  const [loading, setLoading] = useState<boolean>(false);
  const [openConfirmationModal, setOpenConfirmationModal] =
    useState<boolean>(false);

  let openSidebarMobile = 1200;

  const handleOpenConfirmationModal = () => {
    setOpenConfirmationModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setOpenConfirmationModal(false);
  };

  const handleLogout = () => {
    setLoading(true);
    LogoutService()
      .then(() => {
        dispatch(resetTheme());
        dispatch(resetAuthReducer());
        dispatch(resetCountReducer());
        dispatch(resetQuestionnaireReducer());
        toastMessage("success", "Logged out successfully");
      })
      .catch((err) => {})
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <div className="text-center w-full flex flex-col items-start">
        <div className={classNames(styles.logoTopContainerFull, "w-full px-2")}>
          <div
            className={classNames(
              "flex items-center w-full",
              styles.logoContainer
            )}
          >
            <Link to={routeConstant.dashboard.path}>
              {width < openSidebarMobile ? (
                <Icons.LogoNoText />
              ) : (
                <Icons.LogoABR />
              )}
            </Link>
          </div>
        </div>
        <div
          className={classNames(
            "flex items-center gap-2",
            styles.userInfoContainer
          )}
        >
          <div className={classNames(styles.avtInitials)}>
            <label>
              {user?.name?.split(" ")?.[0]?.[0]}
              {user?.name?.split(" ")?.[1]?.[0]}
              {user?.name?.split(" ")?.[2]?.[0]}
            </label>
          </div>
          <div className={classNames("flex flex-col items-start")}>
            <p>{user?.name}</p>
          </div>
        </div>
        <div className={classNames("flex flex-col h-full my-6 gap-4")}>
          <div
            className={classNames(styles.sideNavFull, "flex flex-col gap-4 ")}
          >
            {sidebarArr?.map((item, inx) => {
              return (
                <div className="flex flex-col items-start gap-3" key={inx}>
                  <label className={classNames(styles.navTitle)}>
                    {item?.title}
                  </label>

                  <div className="w-full flex flex-col gap-1">
                    {item?.paths?.map(
                      (s: SideBarItemPathType, s_inx: number) => {
                        const props = {
                          ...s,
                        };
                        return <NavItem {...props} key={s_inx} />;
                      }
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div
            className={classNames(
              "flex flex-col gap-3",
              styles.settingsContainer
            )}
          >
            <label className={classNames(styles.navTitle)}>Settings</label>

            <div className={classNames(styles.navItemContainer, "gap-[12px]")}>
              <label className={classNames(styles.themeTitle)}>
                {theme} Theme
              </label>
              <Switch
                toggle={theme === "dark"}
                handleChange={() => {
                  if (theme === "dark") {
                    dispatch(setTheme({ theme: "light" }));
                  } else {
                    dispatch(setTheme({ theme: "dark" }));
                  }
                }}
              />
            </div>
          </div>
          <div
            className={classNames(styles.navItemContainer, "pb-4")}
            onClick={handleOpenConfirmationModal}
          >
            <Icons.Logout className={classNames(styles.logoutIcon)} />
            <label
              className={classNames(
                styles.heading,
                styles.logout,
                "cursor-pointer"
              )}
            >
              Logout
            </label>
          </div>
        </div>
      </div>
      <ConfirmationModal
        show={openConfirmationModal}
        handleClose={handleCloseConfirmationModal}
        title="Are you sure you want to log out?"
        actionButtonText="Yes, Logout"
        handleSubmit={handleLogout}
        loading={loading}
        showSubtitle={false}
      />
    </>
  );
}

export default DashboardSidebar;
